import styled, { css } from 'styled-components';

const FIGMA_FONT = `'Segoe UI', 'Open Sans', 'Raleway', sans-serif`;
const ACTIONS_FONT = `'Poppins', 'Open Sans', sans-serif`;

const getThemeOverlayColor = (color?: string) => {
    if (!color) return 'rgba(37, 37, 163, 0.12)';

    const value = color.trim();
    const shortHex = /^#([0-9a-f]{3})$/i.exec(value);
    if (shortHex) {
        const [r, g, b] = shortHex[1].split('').map((channel) => parseInt(channel + channel, 16));
        return `rgba(${r}, ${g}, ${b}, 0.12)`;
    }

    const longHex = /^#([0-9a-f]{6})$/i.exec(value);
    if (longHex) {
        const normalized = longHex[1];
        const r = parseInt(normalized.slice(0, 2), 16);
        const g = parseInt(normalized.slice(2, 4), 16);
        const b = parseInt(normalized.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 0.12)`;
    }

    const rgb = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i.exec(value);
    if (rgb) {
        return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, 0.12)`;
    }

    const rgba = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[\d.]+\s*\)$/i.exec(value);
    if (rgba) {
        return `rgba(${rgba[1]}, ${rgba[2]}, ${rgba[3]}, 0.12)`;
    }

    return 'rgba(37, 37, 163, 0.12)';
};

type AccentProps = {
    $accentColor?: string;
};

export const StatusBackdropFrame = styled.div<AccentProps>`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    max-width: 984px;
    margin: 0 auto;
    padding: 20px 20px 0;
    overflow: visible;

    &::before {
        content: "";
        position: absolute;
        inset: 0 0 auto 0;
        height: 260px;
        background: ${(props) => getThemeOverlayColor(props.$accentColor)};
        border-radius: 27px;
        pointer-events: none;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        max-width: 960px;
        padding: 12px 8px 0;

        &::before {
            height: 220px;
            border-radius: 20px;
        }
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        padding: 10px 0 0;

        &::before {
            height: 180px;
            border-radius: 16px;
            opacity: 0.8;
        }
    }
`;

export const AveonlineWrapper = styled.div<{ $status: 'success' | 'pending' | 'error' | 'test' }>`
    box-sizing: border-box;
    position: relative;
    isolation: isolate;
    padding: 2rem 4.25rem 2rem;
    max-width: 944px;
    margin: 0 auto;
    background: #fff;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 1.7rem;
    overflow: visible;
    box-shadow: ${(props) => props.$status === 'success' ? 'none' : '5px 14px 20.6px rgba(0, 0, 0, 0.1)'};

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        padding: 1.75rem 2rem 2rem;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        max-width: 100%;
        padding: 1.25rem 1rem 1.5rem;
        border-radius: ${props => props.$status === 'success' ? '0' : '16px'};
        gap: 1.25rem;
        ${(props) => props.$status !== 'success' && css`
            box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.1);
        `}
    }
`;

export const StatusHeader = styled.div`
    text-align: center;
    padding: 0.5rem 0 1.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;

    h2 {
        font-family: ${FIGMA_FONT};
        font-style: normal;
        font-size: 36px;
        font-weight: 400;
        line-height: 1;
        letter-spacing: 0;
        color: #000;
        margin: 0;
        cursor: default;
        user-select: none;
    }

    p {
        font-family: ${FIGMA_FONT};
        font-style: normal;
        font-size: 24px;
        font-weight: 400;
        line-height: 1;
        letter-spacing: 0;
        color: #000;
        margin: 0;
        cursor: default;
        user-select: none;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        h2 { font-size: 26px; line-height: 1; }
        p { font-size: 16px; line-height: 1.15; }
    }
`;

export const StatusBannerCard = styled.div`
    position: relative;
    padding: 1.8rem 1.35rem 0.9rem;
    text-align: center;
    margin: 0;
    overflow: visible;

    h2 {
        font-family: ${FIGMA_FONT};
        font-size: 36px;
        font-weight: 400;
        line-height: 1;
        letter-spacing: 0;
        color: #000;
        margin: 0.75rem 0 0.25rem;
        cursor: default;
        user-select: none;
    }

    p {
        font-family: ${FIGMA_FONT};
        font-size: 24px;
        font-weight: 400;
        line-height: 1.2;
        letter-spacing: 0;
        color: #000;
        margin: 0 auto;
        max-width: 820px;
        cursor: default;
        user-select: none;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        padding: 1.75rem 1.15rem;
        margin: 0;
    }
`;

export const SummaryCard = styled.div`
    border-radius: 10px;
    padding: 10.5px 26px;
    background: #fbfbfb;
`;

export const OrderSummaryCard = styled(SummaryCard)`
    background: #fbfbfb;
    box-shadow: 0px 5px 7.6px rgba(0, 0, 0, 0.1);
    min-height: 244px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const NextStepCard = styled(SummaryCard)`
    background: #fbfbfb;
    box-shadow: 0px 5px 7.6px rgba(0, 0, 0, 0.1);
    padding: 21px 26px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

export const ShadowCard = styled.div`
    border-radius: 10px;
    padding: 2rem 2.5rem;
    background: #FBFBFB;
    border: 1px solid #f3f3f5;
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        padding: 1.75rem 2rem;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        padding: 1.25rem 1.15rem;
        gap: 1rem;
    }
`;

export const CardRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 80px;
    width: 100%;
    max-width: 701px;
    margin: 0 auto;

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        gap: 48px;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        flex-direction: column;
        gap: 1rem;
        max-width: 100%;
    }
`;

export const ShippingSummaryCard = styled.div`
    background: #fbfbfb;
    border-radius: 15px;
    padding: 32px 0;
    min-height: 205px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;

    > * {
        padding: 5px 18px;
        max-width: 642px;
        margin: 0 auto;
        width: 100%;
        box-sizing: content-box;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        padding: 28px 0;

        > * {
            max-width: 100%;
            padding: 5px 40px;
            box-sizing: border-box;
        }
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        padding: 1.5rem 1rem;
        gap: 1rem;

        > * {
            padding: 0;
            max-width: 100%;
            box-sizing: border-box;
        }
    }
`;

const CardColBase = styled.div`
    display: flex;
    flex-direction: column;
`;

export const CardColLeft = styled(CardColBase)`
    flex: 0 1 360px;
    min-width: 0;
    align-items: flex-start;
    text-align: left;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        flex: 1 1 100%;
    }
`;

export const CardColRight = styled(CardColBase)`
    flex: 0 0 auto;
    min-width: 167px;
    align-items: flex-end;
    text-align: right;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        flex: 1 1 100%;
        align-items: flex-start;
        text-align: left;
    }
`;

export const Label = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 24px;
    font-weight: 600;
    color: #000;
    line-height: 32px;
    margin-bottom: 1.5rem;
    white-space: nowrap;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 20px;
        line-height: 28px;
        margin-bottom: 1rem;
        white-space: normal;
    }
`;

export const SubLabel = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 600;
    color: #000;
    line-height: 27px;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 17px;
        line-height: 22px;
    }
`;

export const OrderNumberRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 350;
    color: #000;
    word-break: break-all;
    margin-bottom: 1.5rem;
    line-height: 1;
    letter-spacing: 0;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 17px;
        margin-bottom: 1rem;
    }
`;

export const CopyButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    color: #000;
    flex-shrink: 0;

    &:hover {
        color: #444;
    }
`;

export const CopiedFeedback = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 0.75rem;
    color: #4caf50;
    font-weight: 600;
    white-space: nowrap;
`;

export const EmailMessage = styled.p`
    font-family: ${FIGMA_FONT};
    font-size: 18px;
    font-weight: 350;
    color: #000;
    line-height: 21px;
    letter-spacing: 0;
    margin: 0;
    max-width: 360px;

    strong {
        font-weight: 600;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 14px;
        line-height: 18px;
        max-width: 100%;
    }
`;

export const Amount = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 700;
    color: #000;
    margin-bottom: 1.5rem;
    line-height: 27px;
    letter-spacing: 0;
`;

export const DateText = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 350;
    color: #000;
    line-height: 1;
    letter-spacing: 0;

    & + & {
        margin-top: 5px;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 16px;
    }
`;

export const StatusTitle = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 600;
    color: #000;
    margin-bottom: 1rem;
    line-height: 27px;
`;

export const StatusDescription = styled.p`
    font-family: ${FIGMA_FONT};
    font-size: 18px;
    font-weight: 350;
    color: #000;
    line-height: 21px;
    letter-spacing: 0;
    margin: 0;
    max-width: 360px;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 15px;
        line-height: 19px;
        max-width: 100%;
    }
`;

export const DayName = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 700;
    color: #000;
    line-height: 27px;
    margin-bottom: 0.75rem;
`;

export const SectionTitle = styled.h3`
    font-family: ${FIGMA_FONT};
    font-style: normal;
    font-size: 30px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: 0;
    color: #000;
    margin: 1rem 0 0.5rem;
    padding-left: 27px;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 22px;
        line-height: 1;
        padding-left: 0;
    }
`;

export const ShippingLabel = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 600;
    color: #000;
    line-height: 27px;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 17px;
        line-height: 22px;
    }
`;

export const ShippingGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.3fr 0.8fr;
    gap: 0.75rem 2rem;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }
`;

export const ShippingGrid2 = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-top: 1rem;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }
`;

export const ShippingCol = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.7rem;

    > ${Label} {
        font-style: normal;
        font-size: 20px;
        font-weight: 600;
        line-height: 1;
        letter-spacing: 0;
        margin-bottom: 0;
    }
`;

export const ShippingValue = styled.span`
    font-family: ${FIGMA_FONT};
    font-style: normal;
    font-size: 18px;
    font-weight: 350;
    color: #000;
    line-height: 1;
    letter-spacing: 0;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 15px;
        line-height: 1.15;
    }
`;

export const ProductsSection = styled.div`
    position: relative;
    box-sizing: border-box;
    border-radius: 0;
    background: #f7f6f6;
    padding: 31px 34px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 320px;
    overflow-y: auto;
    overflow-x: hidden;

    scrollbar-width: thin;
    scrollbar-color: #888 transparent;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        margin: 31px 0;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 2.5px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #666;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        padding: 1rem;
        max-height: 260px;
    }
`;

export const ProductsSectionWrapper = styled.div`
    position: relative;

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background: #d7d7d7;
        border-radius: 2.5px;
        box-shadow: 0px 2px 4.5px rgba(0, 0, 0, 0.25);
        pointer-events: none;
    }
`;

export const ShadowProductsSection = styled.div`
    position: relative;
    border-radius: 6px;
    background: #f7f7f8;
    border: 1px solid #efefef;
    box-shadow: none;
    max-height: 190px;
    min-height: 182px;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: #8b8b8b #f0f0f0;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: #8b8b8b;
        border-radius: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f0f0f0;
    }

    &::after {
        content: '';
        position: sticky;
        bottom: 0;
        display: block;
        height: 8px;
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0));
    }
`;

export const ProductRow = styled.div`
    display: grid;
    grid-template-columns: 220px 90px 90px 75px;
    column-gap: 80px;
    align-items: center;
    justify-content: center;
    padding: 0.25rem 0;

    > *:nth-child(1) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    > *:nth-child(2),
    > *:nth-child(3) {
        white-space: nowrap;
    }

    > *:nth-child(4) {
        justify-self: end;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        grid-template-columns: 200px 80px 80px 75px;
        column-gap: 40px;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        grid-template-columns: 1fr auto 75px;
        column-gap: 1rem;
        row-gap: 0.25rem;

        > *:nth-child(1) {
            grid-column: 1 / -1;
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
        }

        > *:nth-child(2) {
            grid-column: 1 / 2;
        }

        > *:nth-child(3) {
            grid-column: 2 / 3;
        }

        > *:nth-child(4) {
            grid-column: 3 / 4;
            grid-row: 1 / span 2;
            justify-self: end;
            align-self: center;
        }
    }
`;

export const ProductName = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 20px;
    font-weight: 600;
    color: #000;
    line-height: 27px;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 16px;
        line-height: 22px;
    }
`;

export const ProductMeta = styled.span`
    font-family: ${FIGMA_FONT};
    font-size: 18px;
    font-weight: 350;
    color: #000;
    line-height: 24px;
    letter-spacing: 0;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 15px;
        line-height: 20px;
    }
`;

export const ProductThumb = styled.img`
    width: 75px;
    height: 75px;
    object-fit: cover;
    border-radius: 8.347px;
    flex-shrink: 0;
`;

export const FooterRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding-top: 0.75rem;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        flex-direction: column;
        align-items: stretch;
        justify-content: center;
    }
`;

export const TextLink = styled.button<AccentProps>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 20px 0;
    font-family: ${ACTIONS_FONT};
    font-size: 18px;
    font-weight: 500;
    color: ${(props) => props.$accentColor || '#F09252'};
    text-decoration: underline;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    line-height: 27px;
    transition: color 0.15s ease, opacity 0.15s ease;

    &:hover {
        opacity: 0.85;
    }
`;

export const FilledButton = styled.button<AccentProps>`
    background: ${(props) => props.$accentColor || '#F09252'};
    border: 1px solid ${(props) => props.$accentColor || '#F09252'};
    color: #fff;
    border-radius: 5px;
    padding: 20px 40px;
    width: 264px;
    font-family: ${ACTIONS_FONT};
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    line-height: 27px;
    text-align: center;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease, filter 0.15s ease;

    &:hover:not(:disabled) {
        filter: brightness(0.92);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.7;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        width: 100%;
    }
`;

export const PrimaryActionButton = styled(FilledButton)`
    display: block;
    width: 100%;
    max-width: 695px;
    margin-top: 58.32px;
    align-self: center;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        max-width: 100%;
        margin-top: 47.952px;
    }
`;

export const OutlineButton = styled.button<AccentProps>`
    background: #fff;
    border: 1px solid ${(props) => props.$accentColor || '#F09252'};
    color: ${(props) => props.$accentColor || '#F09252'};
    border-radius: 5px;
    padding: 20px 40px;
    width: 264px;
    font-family: ${ACTIONS_FONT};
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    line-height: 27px;
    text-align: center;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

    &:hover {
        background: ${(props) => props.$accentColor || '#F09252'};
        color: #fff;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        width: 100%;
    }
`;

export const ErrorActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 30px;
    gap: 14px;
    padding: 0.5rem 0 0;

    > button:first-child {
        width: auto;
        min-width: 320px;
        max-width: 360px;
        min-height: 67px;
        padding: 20px 32px;
        border-radius: 5px;
        font-family: ${ACTIONS_FONT};
        font-size: 18px;
        font-weight: 700;
        line-height: 27px;
        white-space: nowrap;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        > button:first-child {
            width: 100%;
            min-width: 0;
            max-width: 100%;
            white-space: normal;
        }
    }
`;

export const CancelLink = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-family: ${ACTIONS_FONT};
    font-size: 18px;
    font-weight: 500;
    line-height: 27px;
    color: #000;
    text-decoration: underline;
    &:hover { opacity: 0.85; }
`;

export const ShippingGrid3 = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.3fr 0.8fr;
    gap: 0.75rem 2rem;

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }
`;
